import type { CurrencyCode } from "@app/types/currency";

import IntlProviderService from "../services/intl-provider";

type Dollars = number;
type Cents = number;

const intlProvidersCache: { [key: string]: IntlProviderService } = {};

export class Money {
  protected _nb_units: number;

  public constructor(
    protected _amount_in_cents: number,
    options: { nb_units?: number } = {}
  ) {
    this._nb_units = options.nb_units || 1;
    if (this._nb_units <= 0) {
      throw new Error("Quantity can't be negative or zero");
    }
  }

  public get amount(): number {
    return Math.round(this._amount_in_cents) / 100;
  }

  public get amountPerUnit(): number {
    return this.amount / this._nb_units;
  }

  public getPrice(
    currency_code: CurrencyCode,
    locale: string,
    currency_display?: "symbol" | "code" | "name"
  ): string {
    return formatPrice({
      currency_code,
      locale,
      amount: this.amount,
      with_cents: true,
      currency_display
    });
  }

  public getPricePerUnit(
    currency_code: CurrencyCode,
    locale: string,
    currency_display?: "symbol" | "code" | "name"
  ): string {
    return formatPrice({
      currency_code,
      locale,
      amount: this.amountPerUnit,
      with_cents: true,
      currency_display
    });
  }

  public getCompactPrice(
    currency_code: CurrencyCode,
    locale: string,
    currency_display?: "symbol" | "code" | "name"
  ): string {
    return formatPrice({
      currency_code,
      locale,
      amount: this.amount,
      with_cents: false,
      currency_display,
      compact: true
    });
  }

  public getCompactPricePerUnit(
    currency_code: CurrencyCode,
    locale: string,
    currency_display?: "symbol" | "code" | "name"
  ): string {
    return formatPrice({
      currency_code,
      locale,
      amount: this.amountPerUnit,
      with_cents: false,
      currency_display,
      compact: true
    });
  }

  public getPriceWithoutCents(
    currency_code: CurrencyCode,
    locale: string,
    currency_display?: "symbol" | "code" | "name",
    compact?: boolean
  ): string {
    return formatPrice({
      currency_code,
      locale,
      amount: Math.round(this.amount),
      with_cents: false,
      currency_display,
      compact
    });
  }

  public getPricePerUnitWithoutCents(
    currency_code: CurrencyCode,
    locale: string,
    currency_display?: "symbol" | "code" | "name"
  ): string {
    return formatPrice({
      currency_code,
      locale,
      amount: Math.round(this.amountPerUnit),
      with_cents: false,
      currency_display
    });
  }
}

/**
 * Ceil the amount: if the actual amount is 20.1 => it will return 21.
 *
 * This is useful to give a conservative price to the end-user. We omit cents,
 * but we don't won't to tell a lower price than the actual one.
 */
export class MoneyCeiled extends Money {
  public override get amount(): number {
    return Math.ceil(Math.round(this._amount_in_cents) / 100);
  }

  public override get amountPerUnit(): number {
    return Math.ceil(this.amount / this._nb_units);
  }
}

interface FormatPriceOptions {
  currency_code: CurrencyCode;
  locale: string;
  amount: number;
  with_cents: boolean;
  currency_display: "symbol" | "code" | "name" | undefined;
  compact?: boolean;
}

function formatPrice({
  currency_code,
  with_cents,
  locale: website_locale,
  amount,
  currency_display = "symbol",
  compact = false
}: FormatPriceOptions): string {
  if (isNaN(amount)) {
    return "N/A";
  }

  let formatting_options: Intl.NumberFormatOptions = {
    style: "currency",
    currency: currency_code,
    currencyDisplay: currency_display
  };

  if (!with_cents) {
    formatting_options.minimumFractionDigits = 0;
    formatting_options.maximumFractionDigits = 0;
  }

  if (compact) {
    amount = getCompactRoundUpAmount(amount);
    formatting_options = {
      ...formatting_options,
      ...getCompactFormattingOptions(amount, currency_code)
    };
  }

  const locale = getISOLocale(website_locale, currency_code);

  const price = getIntlProvider(currency_code, locale).formatNumber(
    amount,
    formatting_options
  );

  return formatToCommonLanguage(price, formatting_options);
}

function getIntlProvider(
  currency_code: CurrencyCode,
  locale: string
): IntlProviderService {
  const cache_key = `${currency_code}:${locale}`;

  if (!intlProvidersCache[cache_key]) {
    intlProvidersCache[cache_key] = new IntlProviderService({
      currency: currency_code,
      locale
    });
  }

  return intlProvidersCache[cache_key];
}

export const formatCentsCeiled = (
  price_in_cents: Cents,
  currency: CurrencyCode,
  locale: string
): string => {
  return new MoneyCeiled(price_in_cents * 100).getPriceWithoutCents(
    currency,
    locale
  );
};

export const formatMoneyCeiled = (
  price_in_dollars: Dollars,
  currency: CurrencyCode,
  locale: string
): string => {
  return new MoneyCeiled(price_in_dollars).getPriceWithoutCents(
    currency,
    locale,
    "symbol",
    true
  );
};

/**
 * This function returns the ISO locale that should be used for Intl formating
 *
 * @param website_locale
 * @param currency_code
 */
function getISOLocale(
  website_locale: string,
  currency_code: CurrencyCode
): string {
  if (currency_code === "CLP" && website_locale === "es-419") {
    return "es-cl";
  }
  if (currency_code === "ARS" && website_locale === "es-419") {
    return "es-ar";
  }
  if (currency_code === "ZAR" && website_locale === "en") {
    return "en-za";
  }

  return website_locale;
}

/**
 * This function returns the price how it is commonly displayed to shopping customers online or in shops
 * Sometime the formatted price returns by the Intl.NumberFormat is not the commonly used format but the official one(defined by authorities)
 * example for fr-fr: The formatter returns "2 k €" but in real life prices are displayed like this: "2k€"
 *
 * @param price
 * @param locale
 * @param options
 * @returns
 */
function formatToCommonLanguage(
  price: string,
  options: Intl.NumberFormatOptions
): string {
  if (options.notation === "compact" && options.currency === "EUR") {
    return price.replace(/\s/g, "");
  }

  return price;
}

/**
 * @param amount should not in cents (e.g. The amount for 10$ should be 10 and not 1000 cents)
 */
export function getCompactRoundUpAmount(amount: number): number {
  if (amount > 1000) {
    return Math.ceil(amount / 100) * 100;
  }

  return Math.ceil(amount);
}

/**
 * Function that returns the formatting options required for the compact display of a currency
 *
 * @param amount
 * @param currency_code
 * @returns
 */
function getCompactFormattingOptions(
  amount: number,
  currency_code: CurrencyCode
): Partial<Intl.NumberFormatOptions> {
  switch (currency_code) {
    case "DKK":
      return getDkkCompactFormattingOptions();
    default:
      return getDefaultCompactFormattingOptions(amount);
  }
}

/**
 * 7500 => 7500
 *
 * @returns
 */
function getDkkCompactFormattingOptions(): Partial<Intl.NumberFormatOptions> {
  return {
    style: undefined
  };
}

function getDefaultCompactFormattingOptions(
  amount: number
): Partial<Intl.NumberFormatOptions> {
  const formatting_options: Partial<Intl.NumberFormatOptions> = {
    notation: "compact",
    currencyDisplay: "symbol"
  };

  if (amount >= 1000) {
    /*
     * Allows to display one fractional digit if needed:
     *  11000 => 11k (digit not needed)
     *  11100 => 11.1k instead of 11k
     *  11500 => 11.5k instead of 12k
     *  11800 => 11.8k instead of 12k
     */
    formatting_options.maximumFractionDigits = 1;
  } else {
    formatting_options.maximumFractionDigits = 0;
  }

  return formatting_options;
}
